import { theme } from '@prophecy/theme';
import { Stack, Text, CheckboxGroup, RadioGroup, Ellipsis, Button } from '@prophecy/ui-v3';
import { StyledRadioItem } from '@prophecy/ui-v3/Radio/styled';
import { useMemo } from 'react';
import styled from 'styled-components';

import { LineageSearchAllResults } from './contants';
import { AppliedFilters, SearchFilters } from './types';

const FilterWrap = styled(Stack)`
  overflow: auto;
  height: 100%;
`;

const FilterList = styled.div`
  max-height: 250px;
  padding: ${theme.outlineWidth};
  padding-right: ${theme.spaces.x6};
  overflow: auto;
`;

const StyledCount = styled(Text)`
  flex-shrink: 0;
`;

const ClearButton = styled(Button)`
  padding: 0;
` as unknown as typeof Button;
const StyledRadioGroup = styled(RadioGroup)`
  ${StyledRadioItem} {
    width: 100%;
  }
`;

const StyledCheckboxGroup = styled(CheckboxGroup)`
  label {
    flex-grow: 1;
  }
`;

function FilterItem({ label, count }: { label: string; count: number }) {
  return (
    <Stack direction='horizontal' gap={theme.spaces.x8} align='space-between' width='100%' alignY='center'>
      <Ellipsis tooltip={true}>{label}</Ellipsis>
      {count >= 0 && (
        <StyledCount color={theme.colors.gray700} level='sm'>
          {count}
        </StyledCount>
      )}
    </Stack>
  );
}

export function LineageSearchFilter({
  filters,
  appliedFilters,
  onChange
}: {
  filters: SearchFilters;
  appliedFilters: AppliedFilters;
  onChange: (appliedFilters: AppliedFilters) => void;
}) {
  const { entityFilters, projectFilters, totalNameHits, totalColumnHits } = filters;

  // Filter out "Columns" and sort by hitCount descending
  const filteredAndSortedEntityFilters = useMemo(() => {
    return entityFilters.filter((filter) => filter.entityKind !== 'Columns').sort((a, b) => b.hitCount - a.hitCount);
  }, [entityFilters]);

  const totalResultsCount = totalNameHits + totalColumnHits;
  const entityOptions = useMemo(() => {
    if (!totalResultsCount) return [];

    return [
      {
        label: <FilterItem label='All' count={totalResultsCount} />,
        value: LineageSearchAllResults
      },
      ...filteredAndSortedEntityFilters.map((filter) => ({
        label: <FilterItem label={filter.displayName} count={filter.hitCount} />,
        value: filter.entityKind
      }))
    ];
  }, [filteredAndSortedEntityFilters, totalResultsCount]);

  const projectOptions = useMemo(
    () =>
      projectFilters.map((filter) => ({
        label: <FilterItem label={filter.projectName} count={filter.hitCount} />,
        value: filter.projectId.toString()
      })),
    [projectFilters]
  );

  const handleEntityChange = (value: string) => {
    if (value === LineageSearchAllResults) {
      onChange({ ...appliedFilters, entityKinds: [LineageSearchAllResults] });
    } else {
      onChange({ ...appliedFilters, entityKinds: [value] });
    }
  };

  const handleProjectChange = (values: string[]) => {
    onChange({ ...appliedFilters, projectIds: values.map(Number) });
  };

  const clearProjectFilters = () => {
    onChange({ ...appliedFilters, projectIds: [] });
  };

  return (
    <FilterWrap gap={theme.spaces.x24}>
      <Stack gap={theme.spaces.x12}>
        <Text level='sm'>Type</Text>
        <FilterList>
          <StyledRadioGroup
            options={entityOptions}
            value={appliedFilters.entityKinds[0] ?? LineageSearchAllResults}
            orientation='vertical'
            width='100%'
            onChange={handleEntityChange}
          />
        </FilterList>
      </Stack>

      {projectOptions.length > 0 && (
        <Stack gap={theme.spaces.x12}>
          <Stack direction='horizontal' align='space-between' alignY='center'>
            <Text level='sm'>Project</Text>
            {appliedFilters.projectIds.length > 0 && (
              <ClearButton
                variant='linkGray'
                onClick={clearProjectFilters}
                data-test-id='lineage-search-filter-clear-button'>
                Clear
              </ClearButton>
            )}
          </Stack>
          <FilterList>
            <StyledCheckboxGroup
              options={projectOptions}
              value={appliedFilters.projectIds.map(String)}
              onChange={handleProjectChange}
            />
          </FilterList>
        </Stack>
      )}
    </FilterWrap>
  );
}
